import React from 'react';
import { Table, Button } from 'antd';
import { Warehouse, Download, Check, X } from 'lucide-react';
import { StatusTag, EmptyState, CollapsibleCard } from '../../../common';
import { buildInventoryRows, summarizeInventory, downloadInventory } from './inventoryTable.controller';

/** Compact "label / value" figure for the summary row. */
const Figure = ({ label, value, className = 'text-surface-900' }) => (
    <div>
        <span className="block text-xs text-surface-500">{label}</span>
        <span className={`block text-[15px] font-semibold tabular-nums ${className}`}>{value}</span>
    </div>
);

const InventoryColumns = [
    {
        title: 'Producto',
        dataIndex: 'name',
        key: 'name',
        width: 280,
        render: (text, record) => (
            <div className="flex flex-wrap items-center gap-2">
                <span className="text-sm font-medium text-surface-900">{text}</span>
                {record.isEmergency && <StatusTag tone="warning">Bajo</StatusTag>}
            </div>
        ),
        sorter: (a, b) => (a.name || '').localeCompare(b.name || '')
    },
    {
        title: 'Código',
        dataIndex: 'productId',
        key: 'productId',
        width: 140,
        render: (text) => <span className="font-mono text-xs text-surface-500">{text}</span>
    },
    {
        title: 'Cantidad',
        dataIndex: 'quantity',
        key: 'quantity',
        width: 130,
        align: 'right',
        render: (text, record) => (
            <StatusTag tone={record.isEmergency ? 'warning' : text > 0 ? 'success' : 'neutral'} className="tabular-nums">
                {text} {text === 1 ? 'bulto' : 'bultos'}
            </StatusTag>
        ),
        sorter: (a, b) => a.quantity - b.quantity
    },
    {
        title: 'Estado',
        dataIndex: 'available',
        key: 'available',
        width: 150,
        align: 'center',
        render: (available) =>
            available ? (
                <StatusTag tone="success" icon={<Check />}>
                    Disponible
                </StatusTag>
            ) : (
                <StatusTag tone="danger" icon={<X />}>
                    No Disponible
                </StatusTag>
            ),
        filters: [
            { text: 'Disponible', value: true },
            { text: 'No Disponible', value: false }
        ],
        onFilter: (value, record) => record.available === value
    }
];

/**
 * Warehouse inventory of a store check. `extraColumns` (antd columns over the rows of
 * buildInventoryRows) are appended after the base ones: the seam company pages use to show
 * data only their backend captures.
 */
function InventoryTable({ inventory, loading, extraColumns }) {
    const inventoryRows = buildInventoryRows(inventory);
    const { totalItems, availableItems, emergencyItems } = summarizeInventory(inventoryRows);

    return (
        <CollapsibleCard
            title="Inventario de Almacén"
            icon={<Warehouse />}
            iconTone="violet"
            flush
            badges={
                <>
                    <StatusTag tone="neutral">{totalItems} productos</StatusTag>
                    {emergencyItems > 0 && <StatusTag tone="warning">{emergencyItems} emergencia</StatusTag>}
                </>
            }>
            {/* Summary row */}
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 px-5 py-4 border-b border-surface-100">
                <div className="flex flex-wrap gap-8">
                    <Figure label="Total" value={totalItems} />
                    <Figure label="Disponibles" value={availableItems} className="text-success-fg" />
                    <Figure label="No Disponibles" value={totalItems - availableItems} className="text-danger-fg" />
                    {emergencyItems > 0 && <Figure label="En Emergencia" value={emergencyItems} className="text-warning-fg" />}
                </div>
                <Button icon={<Download />} onClick={() => downloadInventory(inventoryRows)}>
                    Exportar
                </Button>
            </div>

            <Table
                size="middle"
                loading={loading}
                dataSource={inventoryRows}
                columns={extraColumns?.length ? [...InventoryColumns, ...extraColumns] : InventoryColumns}
                pagination={{
                    pageSize: 15,
                    showTotal: (total) => `Total ${total} productos`
                }}
                scroll={{ x: 640 }}
                locale={{ emptyText: <EmptyState icon={<Warehouse />} title="Sin inventario" description="No hay inventario registrado." /> }}
            />
        </CollapsibleCard>
    );
}

export default InventoryTable;
